import { Button, ManageableItemsList } from '@payaca/ambrosia';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { FC, useReducer, useState, type JSX } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';

import useGetStockLocations from '@/api/queries/stockLocations/useGetStockLocations';
import { useCanUpdateAccount } from '@/hooks/useCanUpdateAccount';
import ConfirmArchiveStockLocationModal from '@/ui/components/companySettings/ConfirmArchiveStockLocationModal';
import CreateEditStockLocationModal, {
  StockLocationListItem,
} from '@/ui/components/companySettings/CreateEditStockLocationModal';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';

const PAGE_SIZE = 100;

// The open modal and its target Stock Location are interrelated, so they live
// in one reducer rather than several useState calls.
type ModalState =
  | { type: 'closed' }
  | { type: 'create' }
  | { type: 'edit'; stockLocation: StockLocationListItem }
  | { type: 'archive'; stockLocation: StockLocationListItem };

type ModalAction =
  | { type: 'create' }
  | { type: 'edit'; stockLocation: StockLocationListItem }
  | { type: 'archive'; stockLocation: StockLocationListItem }
  | { type: 'close' };

const modalReducer = (_state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'create':
      return { type: 'create' };
    case 'edit':
      return { type: 'edit', stockLocation: action.stockLocation };
    case 'archive':
      return { type: 'archive', stockLocation: action.stockLocation };
    case 'close':
      return { type: 'closed' };
  }
};

const ListedStockLocationsPage: FC = (): JSX.Element => {
  const readOnly = !useCanUpdateAccount();
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [modal, dispatchModal] = useReducer(modalReducer, { type: 'closed' });

  const { stockLocations, isLoading } = useGetStockLocations(
    {},
    { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }
  );

  const closeModal = () => dispatchModal({ type: 'close' });

  return (
    <AuthenticatedPageWrapper>
      <Helmet title="Stock locations" />
      <div className="mb-16 p-4">
        <ManageableItemsList>
          <ManageableItemsList.HeaderBar
            heading="Stock locations"
            buttons={
              <Button
                onClick={() => dispatchModal({ type: 'create' })}
                disabled={readOnly}
                size="sm"
              >
                Create stock location
              </Button>
            }
          />
          <ManageableItemsList.Table
            isLoading={isLoading}
            items={stockLocations?.items ?? []}
            uniqueKey="id"
            emptyText="No stock locations yet."
            onClickRow={(row) => history.push(`/stock-locations/${row.id}`)}
            itemActions={
              readOnly
                ? undefined
                : [
                    {
                      label: 'Edit',
                      onClick: (row) =>
                        dispatchModal({ type: 'edit', stockLocation: row }),
                    },
                    {
                      label: 'Archive',
                      onClick: (row) =>
                        dispatchModal({ type: 'archive', stockLocation: row }),
                    },
                  ]
            }
          >
            <ManageableItemsList.Table.Column<StockLocationListItem, 'name'>
              header="Name"
              field="name"
            />
            <ManageableItemsList.Table.Column<StockLocationListItem, 'address'>
              header="Address"
              field="address"
              render={(address) =>
                getAddressAsString({
                  line1: address.line1 ?? undefined,
                  line2: address.line2 ?? undefined,
                  city: address.city ?? undefined,
                  administrativeArea: address.administrativeArea ?? undefined,
                  postcode: address.postalCode ?? undefined,
                }) || '-'
              }
            />
          </ManageableItemsList.Table>
          <ManageableItemsList.PaginationBar
            type="custom"
            pageSize={PAGE_SIZE}
            currentPage={page}
            totalItems={stockLocations?.totalCount ?? 0}
            onPageChange={setPage}
          />
        </ManageableItemsList>

        <CreateEditStockLocationModal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          stockLocation={
            modal.type === 'edit' ? modal.stockLocation : undefined
          }
        />
        <ConfirmArchiveStockLocationModal
          isOpen={modal.type === 'archive'}
          onClose={closeModal}
          stockLocation={
            modal.type === 'archive' ? modal.stockLocation : undefined
          }
        />
      </div>
    </AuthenticatedPageWrapper>
  );
};

export default ListedStockLocationsPage;
